import React, { useEffect, useState } from "react";
import * as LocalStore from "../services/LocalStore";
import { useToast } from "./ToastProvider";

type Contract = {
  name?: string;
  startDate?: string;
  endDate?: string;
  totalHours?: number;
};

type Line = { weekStart: string; hours: number; sum: number; balance: number };

export default function Projections({
  initialContract,
}: {
  initialContract?: string | undefined;
}) {
  const [calendars, setCalendars] = useState<
    Array<{ name?: string; type?: string }>
  >([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selectedContract, setSelectedContract] = useState(
    initialContract ?? ""
  );
  const [lines, setLines] = useState<Line[]>([]);
  const [meta, setMeta] = useState<string>("");
  const [enabledCals, setEnabledCals] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async function () {
      await Promise.all([loadContracts(), loadCalendars()]);
      let initial = initialContract;
      try {
        const q = new URL(window.location.href).searchParams.get("name");
        if (q) initial = q;
      } catch (e) {}
      if (initial) {
        setSelectedContract(initial);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toast = useToast();

  async function loadCalendars() {
    const data = await LocalStore.getCalendars();
    const list = data ?? [];
    setCalendars(list);
    setEnabledCals(new Set(list.map((c) => (c.name ?? "").toLowerCase())));
  }

  async function loadContracts() {
    const data = await LocalStore.getContracts();
    const cs = data ?? [];
    setContracts(cs);
    if (!selectedContract && cs.length) setSelectedContract(cs[0].name ?? "");
  }

  // If contracts load and nothing is selected, auto-select the first one.
  useEffect(() => {
    if (!selectedContract && contracts.length) {
      const first = contracts[0].name ?? "";
      if (first) setSelectedContract(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts]);

  // Load projection whenever selectedContract changes
  useEffect(() => {
    if (selectedContract) {
      loadProjection(selectedContract);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContract]);

  async function loadProjection(name?: string) {
    const n = name ?? selectedContract;
    if (!n) return;
    const enabled = Array.from(enabledCals.values());
    const data = await LocalStore.getProjection(n, enabled);
    if (!data) {
      toast.show("Failed to load projection", "error");
      return;
    }
    function fmtDate(s?: string) {
      if (!s) return "";
      const d = new Date(s);
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
    const c = data.contract;
    setMeta(
      `${c.name} — ${fmtDate(c.startDate)} → ${fmtDate(c.endDate)} — ${
        c.totalHours
      } hours`
    );
    setLines(data.lines);
  }

  function onToggleCalendar(name?: string, checked?: boolean) {
    if (!name) return;
    const key = name.toLowerCase();
    const copy = new Set(enabledCals);
    if (checked === undefined) checked = !copy.has(key);
    if (checked) copy.add(key);
    else copy.delete(key);
    setEnabledCals(copy);
    if (selectedContract) {
      LocalStore.getProjection(
        selectedContract,
        Array.from(copy.values())
      ).then((d) => {
        if (!d) return;
        setMeta(
          `${d.contract.name} — ${new Date(
            d.contract.startDate!
          ).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })} → ${new Date(d.contract.endDate!).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })} — ${d.contract.totalHours} hours`
        );
        setLines(d.lines);
      });
    }
  }

  return (
    <div className="projections">
      <h1>Projections</h1>
      <div className="controls">
        <div>
          <label>Contract</label>
          {contracts.length === 0 ? (
            <div className="muted">
              No contracts found. Add one under the Contracts section.
            </div>
          ) : (
            <select
              id="contract"
              value={selectedContract}
              onChange={(e) => setSelectedContract(e.target.value)}
            >
              {contracts.map(
                (c) =>
                  c.name && (
                    <option key={c.name} value={c.name}>{`${
                      c.name
                    } (${c.startDate?.slice(0, 10)} → ${c.endDate?.slice(
                      0,
                      10
                    )})`}</option>
                  )
              )}
            </select>
          )}
        </div>
      </div>

      <div className="calendars">
        <ul id="calendarList">
          {calendars.map((c) => {
            const lower = (c.name ?? "").toLowerCase();
            const checked = enabledCals.has(lower);
            const typeClass = (c.type ?? "").toLowerCase(); // e.g. 'workdays' or 'nonworkdays'
            return (
              <li
                key={c.name}
                className={`calendar-pill ${typeClass} ${
                  checked ? "included" : "excluded"
                }`}
              >
                <label className="pill-label">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onToggleCalendar(c.name, e.target.checked)}
                    aria-checked={checked}
                  />
                  <span className="pill-content">
                    <span className="checkmark" aria-hidden>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20 6L9 17l-5-5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="cal-name">{c.name}</span>
                    <span className="muted cal-type">{c.type}</span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="muted" id="meta">
        {meta}
      </div>
      <table
        id="table"
        aria-label="Weekly projection"
        hidden={lines.length === 0}
      >
        <thead>
          <tr>
            <th>Week Start</th>
            <th className="num">Hours</th>
            <th className="num">Sum</th>
            <th className="num">Declining Balance</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((ln, idx) => (
            <tr key={idx}>
              <td>{ln.weekStart}</td>
              <td className="num">{ln.hours.toFixed(2)}</td>
              <td className="num">{ln.sum.toFixed(2)}</td>
              <td className="num">{ln.balance.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
